import { isNumber } from "./type-guards.js";
const recoverableStatusCodes = /* @__PURE__ */ new Set([
  500,
  // Internal Server Error
  502,
  // Bad Gateway
  503,
  // Service Unavailable
  429
  // Too Many Requests
]);
const shouldRetry = (response) => {
  if (response.ok) {
    return false;
  }
  return recoverableStatusCodes.has(response.status);
};
const httpMethodsWeCanRetry = /* @__PURE__ */ new Set(["GET", "PUT", "HEAD", "DELETE", "OPTIONS", "TRACE"]);
const canRetry = (request) => {
  if (!httpMethodsWeCanRetry.has(request?.method ?? "GET")) {
    return false;
  }
  return true;
};
const pause = (time) => new Promise((resolve) => {
  setTimeout(() => resolve(), time);
});
const fetchWithTimeout = async (timeout, ...fetchArgs) => {
  const controller = new AbortController();
  const id = setTimeout(() => {
    controller.abort();
  }, timeout);
  const [requestInfo, requestInit = {}] = fetchArgs;
  const response = await fetch(requestInfo, {
    ...requestInit,
    signal: controller.signal
  });
  clearTimeout(id);
  return response;
};
const defaultOptions = {
  attempts: [
    // First attempt, allow 10s to respond
    { timeout: 1e4 },
    // Wait 2s before trying again, waiting 10s again
    { wait: 2e3, timeout: 1e4 },
    // Wait 5s more seconds for third attempt, leave just 3s for server to respond
    { wait: 5e3, timeout: 3e3 }
  ]
};
const resilientFetch = (options) => {
  const { attempts } = {
    ...defaultOptions,
    ...options
  };
  return async (info, init) => {
    const url = typeof info === "string" ? info : info.url;
    if (!canRetry(init)) {
      return fetch(info, init);
    }
    for (let a = 0; a < attempts.length; a++) {
      const attempt = attempts[a];
      if (isNumber(attempt?.wait)) {
        console.info(`Retrying ${url} in ${attempt?.wait}ms`);
        await pause(attempt?.wait ?? 0);
      }
      let response = null;
      try {
        response = await fetchWithTimeout(attempt?.timeout ?? 0, info, init);
      } catch (e) {
        let message = String(e);
        if (e instanceof Error && e.name === "AbortError") {
          message = `request took longer than ${attempt?.timeout}ms`;
        }
        if (a < attempts.length - 1) {
          console.warn(`Attempt #${a + 1} to fetch ${url} failed: `, message);
        }
      }
      if (!response || shouldRetry(response)) {
        continue;
      }
      return response;
    }
    throw new Error(`Failed to fetch '${url}' after ${attempts.length} attempts`);
  };
};
export {
  resilientFetch
};
//# sourceMappingURL=resilient-fetch.js.map
